import React, { useEffect, useState } from "react";
import { collection, doc, getDoc, getDocs, serverTimestamp, setDoc, updateDoc } from "firebase/firestore";
import { AlertTriangle, CheckCircle2, ShieldOff, SlidersHorizontal } from "lucide-react";
import { toast } from "sonner";
import { ADMIN_EMAILS, CONTROL_EMAILS, db } from "../../firebase";
import { useAuth } from "../../contexts/AuthContext";

const defaultSettings = {
  registrationEnabled: true,
  paymentsEnabled: true,
};

const AdminControls = () => {
  const { profile } = useAuth();
  const [settings, setSettings] = useState(defaultSettings);
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const canControl = !!profile?.hasControls;

  const load = async () => {
    setLoading(true);
    try {
      const settingsSnap = await getDoc(doc(db, "settings", "platform"));
      setSettings(settingsSnap.exists() ? { ...defaultSettings, ...settingsSnap.data() } : defaultSettings);

      const usersSnap = await getDocs(collection(db, "users"));
      const adminRows = usersSnap.docs
        .map((item) => ({ uid: item.id, ...item.data() }))
        .filter((item) => ADMIN_EMAILS.includes(String(item.email || "").toLowerCase()) || item.role === "admin")
        .sort((a, b) => String(a.email || "").localeCompare(String(b.email || "")));
      setAdmins(adminRows);
    } catch (error) {
      toast.error(error.message || "Unable to load controls");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const saveSetting = async (key, value) => {
    if (!canControl) return;
    const next = { ...settings, [key]: value };
    setSettings(next);
    try {
      await setDoc(doc(db, "settings", "platform"), {
        ...next,
        updatedAt: serverTimestamp(),
        updatedBy: profile?.email || "",
      }, { merge: true });
      toast.success("Setting updated");
    } catch (error) {
      setSettings(settings);
      toast.error(error.message || "Unable to update setting");
    }
  };

  const updateAdminStatus = async (adminUser, status) => {
    if (!canControl) return;
    const email = String(adminUser.email || "").toLowerCase();
    if (CONTROL_EMAILS.includes(email)) {
      toast.error("This account cannot be disabled here.");
      return;
    }
    try {
      await updateDoc(doc(db, "users", adminUser.uid), {
        status,
        updatedAt: serverTimestamp(),
        updatedBy: profile?.email || "",
      });
      toast.success(status === "disabled" ? "Admin disabled" : "Admin enabled");
      await load();
    } catch (error) {
      toast.error(error.message || "Unable to update admin");
    }
  };

  if (!canControl) {
    return (
      <div className="rounded-xl border border-amber-200 bg-amber-50 p-6 text-amber-900">
        <div className="flex items-center gap-2 font-heading text-lg font-bold">
          <AlertTriangle className="h-5 w-5" /> Access restricted
        </div>
        <p className="mt-2 text-sm">This area is not available for this admin account.</p>
      </div>
    );
  }

  return (
    <div data-testid="admin-controls">
      <div className="mb-8">
        <div className="mb-2 text-xs font-bold uppercase tracking-[0.2em] text-blue-600">Controls</div>
        <h1 className="font-heading text-3xl font-bold tracking-tight text-slate-900">Platform Controls</h1>
        <p className="mt-1 text-slate-600">Manage registration, payment availability, and admin access.</p>
      </div>

      {loading ? (
        <div className="rounded-xl border border-slate-200 bg-white p-10 text-center text-slate-500">Loading...</div>
      ) : (
        <div className="grid gap-6 lg:grid-cols-2">
          <ControlCard
            title="Registration"
            description="Turn this off to stop new student registrations."
            enabled={settings.registrationEnabled}
            onChange={(value) => saveSetting("registrationEnabled", value)}
          />
          <ControlCard
            title="Payments"
            description="Turn this off to stop new payment order creation."
            enabled={settings.paymentsEnabled}
            onChange={(value) => saveSetting("paymentsEnabled", value)}
          />

          <div className="rounded-xl border border-slate-200 bg-white p-5 lg:col-span-2">
            <div className="mb-4 flex items-center gap-2">
              <SlidersHorizontal className="h-5 w-5 text-blue-600" />
              <h2 className="font-heading text-xl font-bold text-slate-900">Admin Access</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-slate-500">
                  <tr>
                    <th className="py-2 font-semibold">Email</th>
                    <th className="py-2 font-semibold">Status</th>
                    <th className="py-2 text-right font-semibold">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {admins.map((item) => {
                    const email = String(item.email || "").toLowerCase();
                    const protectedAccount = CONTROL_EMAILS.includes(email);
                    const disabled = item.status === "disabled";
                    return (
                      <tr key={item.uid || email} className="border-t border-slate-100">
                        <td className="py-3 font-medium text-slate-900">{email}</td>
                        <td className="py-3">
                          <span className={`rounded-full px-2 py-1 text-xs font-semibold ${disabled ? "bg-red-50 text-red-700" : "bg-emerald-50 text-emerald-700"}`}>
                            {disabled ? "Disabled" : "Active"}
                          </span>
                        </td>
                        <td className="py-3 text-right">
                          {protectedAccount ? (
                            <span className="text-xs text-slate-400">Locked</span>
                          ) : (
                            <button
                              type="button"
                              onClick={() => updateAdminStatus(item, disabled ? "active" : "disabled")}
                              className={`inline-flex items-center gap-2 rounded-lg px-3 py-2 text-xs font-semibold ${
                                disabled ? "bg-emerald-600 text-white hover:bg-emerald-700" : "bg-red-50 text-red-700 hover:bg-red-100"
                              }`}
                            >
                              {disabled ? <CheckCircle2 className="h-4 w-4" /> : <ShieldOff className="h-4 w-4" />}
                              {disabled ? "Enable" : "Disable"}
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const ControlCard = ({ title, description, enabled, onChange }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-5">
    <div className="flex items-start justify-between gap-4">
      <div>
        <h2 className="font-heading text-xl font-bold text-slate-900">{title}</h2>
        <p className="mt-1 text-sm leading-6 text-slate-600">{description}</p>
      </div>
      <button
        type="button"
        onClick={() => onChange(!enabled)}
        className={`relative h-8 w-14 flex-shrink-0 rounded-full transition-colors ${enabled ? "bg-emerald-500" : "bg-slate-300"}`}
        aria-label={`Toggle ${title}`}
      >
        <span className={`absolute top-1 h-6 w-6 rounded-full bg-white shadow transition-transform ${enabled ? "translate-x-7" : "translate-x-1"}`}></span>
      </button>
    </div>
    <div className={`mt-4 inline-flex rounded-full px-3 py-1 text-xs font-bold ${enabled ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"}`}>
      {enabled ? "Enabled" : "Stopped"}
    </div>
  </div>
);

export default AdminControls;
